import React, { useState } from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import {
  countDadosCadastrais,
  formatDocument,
  formatToReal,
} from "../../../utils/utils";
import Sociedade from "../../common/query/Sociedade";
import { LuBuilding2 } from "react-icons/lu";
import { FiPhone, FiUsers } from "react-icons/fi";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { MdOutlineEmail } from "react-icons/md";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const CadastralPremiumPJQuery = ({ data, dataFull }) => {
  const dc = data?.dadosCadastrais || {};
  const [copied, setCopied] = useState({ cnpj: false, cep: false });

  const handleCopy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied((p) => ({ ...p, [key]: true }));
      setTimeout(() => setCopied((p) => ({ ...p, [key]: false })), 1200);
    } catch (_) {}
  };

  const capitalMostrar =
    typeof dc?.capitalInicial === "number"
      ? formatToReal(dc.capitalInicial)
      : (dc?.capitalInicial || "").toString().trim().startsWith("R$")
        ? dc.capitalInicial
        : dc?.capitalInicial
          ? formatToReal(
              Number(
                String(dc.capitalInicial)
                  .replace(/[^\d,.-]/g, "")
                  .replace(/\./g, "")
                  .replace(",", "."),
              ),
            )
          : "Indisponível";
  const foundationMoment = dc?.dataFundacao
    ? moment(dc.dataFundacao, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"], true)
    : null;
  const foundationLabel =
    foundationMoment && foundationMoment.isValid()
      ? foundationMoment.format("DD/MM/YYYY")
      : null;
  const foundationAgeLabel = buildDurationLabel(
    foundationMoment,
    "de atividade",
  );
  const situacaoLabel = dc?.situacao || "";
  const participacoesPorSocios = [
    data?.participacoesPorSocios,
    data?.participacoesPorSocio,
    data?.participacaoPorSocios,
    data?.participacoesSocios,
    data?.participacoes_socios,
    data?.participacoesPorSociosAdm,
  ].find((item) => Array.isArray(item));
  const summaryCards = [
    {
      label: "CNAE / ramo",
      value: dc?.ramoAtividade || "Não informado",
      hint: "Atividade",
    },
    {
      label: "Natureza jurídica",
      value: dc?.naturezaJuridica || dc?.natureza || "Não informado",
      hint: "Perfil",
    },
    {
      label: "Capital social",
      value: capitalMostrar,
      hint: "Recursos iniciais",
    },
    {
      label: "Inscrição Estadual",
      value: dc?.inscricaoEstadual || "Não informado",
      hint: "Registro",
    },
  ];

  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  // Só renderiza se houver exatamente 1
  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 2;

  return (
    <>
      {podeRenderizarDadosCadastrais && (
        <>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <LuBuilding2 className="text-blue-700" />{" "}
              <span>Dados Cadastrais</span>
            </div>
          </TitleStripe>
          <div className="text-xs">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  Razão Social
                </p>
                <p className="text-xl font-semibold text-gray-900">
                  {dc?.razaoSocial || "Indisponível"}
                </p>
                <p className="text-[11px] text-gray-500">
                  {dc?.nomeFantasia || "Sem nome fantasia"}
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Situação: {situacaoLabel || "Desconhecida"}
              </span>
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Fundação: {foundationLabel || "—"}
              </span>
              {foundationAgeLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  {foundationAgeLabel}
                </span>
              )}
              {dc?.cidade && dc?.uf && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-700">
                  📍 {dc.cidade}/{dc.uf}
                </span>
              )}
            </div>

            <div className="mt-3 grid gap-2 grid-cols-4">
              {summaryCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {data?.socios && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiUsers className="text-blue-700" />
              <span>Quadro Societário</span>
            </div>
          </TitleStripe>
          {data?.socios?.length > 0 ? (
            <Sociedade socios={data?.socios} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.administradores && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiUsers className="text-blue-700" />
              <span>Quadro de Administradores</span>
            </div>
          </TitleStripe>
          {data?.administradores?.length > 0 ? (
            <Sociedade socios={data?.administradores} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {participacoesPorSocios && (
        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <FiUsers className="text-blue-700" />
              <span>Participações por sócios</span>
            </div>
          </TitleStripe>

          {participacoesPorSocios.length > 0 ? (
            <div className="space-y-4">
              {participacoesPorSocios.map((socio, index) => (
                <div
                  key={`${socio?.socioCpfCnpj || socio?.socioNome || "socio"}-${index}`}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {socio?.socioNome || "Sócio não identificado"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDocument(socio?.socioCpfCnpj) ||
                        socio?.socioCpfCnpj ||
                        "Documento não informado"}
                    </p>
                  </div>

                  {Array.isArray(socio?.participacoes) &&
                  socio.participacoes.length > 0 ? (
                    <ParticipacaoEmEmpresas empresas={socio.participacoes} />
                  ) : (
                    <NoOcurrence />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <HiOutlineLocationMarker className="text-blue-700" />{" "}
              <span>Histórico de Endereços</span>
            </div>
          </TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <MdOutlineEmail className="text-blue-700" /> <span>Histórico de E-mails</span>
            </div>
          </TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiPhone className="text-blue-700" /> <span>Histórico de Telefones</span>
            </div>
          </TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </>
  );
};

export default CadastralPremiumPJQuery;
